import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Download, Calendar, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConference } from '@/context/ConferenceContext';
import api from '@/services/api';



const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { importantDetails, siteConfig, getConferenceVenue } = useConference();

  const provider = searchParams.get('provider');
  const token = provider === 'paypal' ? searchParams.get('paymentId') : searchParams.get('session_id');
  const payerId = searchParams.get('PayerID');
  const registrationId = searchParams.get('registration_id');
  const username = searchParams.get('username');


  useEffect(() => {
    const confirmAndFetch = async () => {
      // Check if this payment has already been confirmed
      const confirmationKey = `payment_confirmed_${token}`;
      const alreadyConfirmed = localStorage.getItem(confirmationKey);
      
      if (alreadyConfirmed === 'true') {
        console.log('Payment already confirmed, skipping API call');
        setPaymentStatus('Payment already confirmed.');
        // Fetch registration details if registrationId is present
        if (registrationId) {
          try {
            const response = await api.get(`/registrations/${registrationId}`);
            setRegistration(response.data);
          } catch (error) {
            console.error('Error fetching registration:', error);
          }
        }
        setLoading(false);
        return;
      }

      let confirmed = false;
      let statusMsg = null;
      try {
        if (token && provider === 'stripe') {
          const res = await api.post('/payment/stripe/success', { token, username });
          confirmed = true;
          statusMsg = res.data.status === 'success' ? 'Payment confirmed by Stripe.' : (res.data.error || 'Stripe payment not completed.');
          if (res.data.status === 'success') {
            localStorage.setItem(confirmationKey, 'true');
          }
        } else if (token && provider === 'paypal') {
          const res = await api.post('/payment/paypal/success', { token, payerId, username });
          confirmed = true;
          statusMsg = res.data.status === 'success' ? 'Payment confirmed by PayPal.' : (res.data.error || 'PayPal payment not completed.');
          if (res.data.status === 'success') {
            localStorage.setItem(confirmationKey, 'true');
          }
        }
      } catch (err) {
        statusMsg = 'Payment confirmation error: ' + (err?.response?.data?.error || err.message);
        console.error('Payment confirmation error:', err);
      }
      // Fallback: If not confirmed, try updating status by token
      if (!confirmed && token) {
        try {
          const res = await api.post('/payment/update-status-by-token', { token });
          statusMsg = res.data.status === 'updated' ? 'Payment status updated in system.' : (res.data.error || 'Could not update payment status.');
          if (res.data.status === 'updated') {
            localStorage.setItem(confirmationKey, 'true');
          }
        } catch (fallbackErr) {
          statusMsg = 'Fallback status update error: ' + (fallbackErr?.response?.data?.error || fallbackErr.message);
          console.error('Fallback status update error:', fallbackErr);
        }
      }
      setPaymentStatus(statusMsg);
      // Fetch registration details if registrationId is present
      if (registrationId) {
        try {
          const response = await api.get(`/registrations/${registrationId}`);
          setRegistration(response.data);
        } catch (error) {
          console.error('Error fetching registration:', error);
        }
      }
      setLoading(false);
    };
    confirmAndFetch();
  }, [registrationId, token, provider]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your confirmation...</p>
        </div>
      </div>
    );
  }


  return (
    <>
      <Helmet>
        <title>Payment Successful - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex w-24 h-24 rounded-full bg-green-100 items-center justify-center mb-6 animate-bounce">
              <CheckCircle className="text-green-500" size={56} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Payment Successful!
            </h1>
            <p className="text-xl text-gray-600">
              Thank you for registering for {siteConfig?.fullName || importantDetails?.ConferenceTitle || 'our conference'}
            </p>
            {/* Show Session ID and Payment Status if present */}
            {token && (
              <div className="mt-4">
                <span className="text-gray-600 font-medium">{provider === 'paypal' ? 'Order ID' : 'Session ID'}:</span>
                <span className="ml-2 font-mono text-sky-600 bg-sky-50 px-2 py-1 rounded">{token}</span>
              </div>
            )}
            {paymentStatus && (
              <div className="mt-2">
                <span className="text-gray-600 font-medium">Payment Status:</span>
                <span className="ml-2 text-sky-700">{paymentStatus}</span>
              </div>
            )}
          </div>

          {registration && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
                Registration Confirmed
              </h2>

              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600 font-medium">Registration ID:</span>
                  <span className="font-bold text-sky-500">{registration.id}</span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600 font-medium">Name:</span>
                  <span className="font-semibold text-gray-900">
                    {registration.title} {registration.name}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600 font-medium">Email:</span>
                  <span className="text-gray-900">{registration.email}</span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600 font-medium">Category:</span>
                  <span className="font-semibold text-gray-900 capitalize">
                    {registration.category}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-gray-600 font-medium">Organization:</span>
                  <span className="text-gray-900">{registration.org}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-gray-600 font-medium">Amount Paid:</span>
                  <span className="font-bold text-green-600 text-xl">
                    €{registration.price}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-900 text-lg mb-4 flex items-center gap-2">
              <Mail className="text-sky-500" />
              Next Steps
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-semibold">1</span>
                </div>
                <p className="text-gray-700 flex-1">
                  A confirmation email has been sent to your registered email address with your receipt and registration details.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-semibold">2</span>
                </div>
                <p className="text-gray-700 flex-1">
                  Your conference badge and materials will be available for pickup at the registration desk on the day of the conference.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-semibold">3</span>
                </div>
                <p className="text-gray-700 flex-1">
                  Add the conference dates to your calendar and prepare for an amazing experience!
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl p-6 mb-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <Calendar size={24} />
              <h3 className="font-semibold text-lg">Mark Your Calendar</h3>
            </div>
            <p className="text-sky-50 mb-4">
              {siteConfig?.fullName || importantDetails?.ConferenceTitle || 'Conference'} • {importantDetails?.ConferenceDates || ''}
            </p>
            <p className="text-sky-50">
              📍 {getConferenceVenue()}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="border-2 border-sky-500 text-sky-500 hover:bg-sky-50"
            >
              <Download className="mr-2" size={18} />
              Print Confirmation
            </Button>
            <Link to="/">
              <Button className="w-full bg-sky-500 hover:bg-sky-600">
                Return to Home
              </Button>
            </Link>
            <Link to="/schedule">
              <Button variant="outline" className="w-full">
                View Schedule
              </Button>
            </Link>
          </div>

          <div className="mt-8 p-4 bg-sky-50 rounded-xl text-center">
            <p className="text-sm text-gray-700">
              Need help? Contact us at{' '}
              <a href={`mailto:${siteConfig?.contact?.email || 'info@conference.com'}`} className="text-sky-500 font-semibold hover:underline">
                {siteConfig?.contact?.email || 'info@conference.com'}
              </a>
              {' '}or{' '}
              <a href={`tel:${siteConfig?.contact?.phone || '+1-234-567-8900'}`} className="text-sky-500 font-semibold hover:underline">
                {siteConfig?.contact?.phone || '+1-234-567-8900'}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccess;
